/**
 * Master Tour Calendar v2 persistence helpers.
 * Day tools write the same Firestore calendarTabEntries docs as v1 tabs.
 */

export const V2_CHAT_TAB = 'CHAT';
export const V2_CHAT_DATE = 'tour';
/** Tour-level merch catalog (items + venue cut). Nightly counts stay on MERCH/{date}. */
export const V2_MERCH_CATALOG_DATE = 'tour';
/** Tour-level rider sheets (one area per library row, including vehicle & parking). */
export const V2_RIDER_AREAS_TAB = 'RIDER_AREAS';
export const V2_RIDER_AREAS_DATE = 'tour';

export const V2_CANVAS_TAB_KEY = {
  setlist: 'SETLIST',
  guestlist: 'GUEST LIST',
  contacts: 'CONTACTS',
  day: 'SCHEDULE',
  merch: 'MERCH',
  settle: 'SETTLEMENT',
  expenses: 'EXPENSES',
  riders: 'RIDER_DAILY',
  daysheet: 'SCHEDULE',
};

export function v2TabEntryId(tourId, tabKey, dateKey) {
  return `${String(tourId || '')}__${String(tabKey || '')}__${String(dateKey || '')}`;
}

export const CHAT_CHANNEL_META = [
  { id: 'party', name: 'Party-wide' },
  { id: 'travel', name: 'Travel & logistics' },
  { id: 'prod', name: 'Production' },
  { id: 'hosp', name: 'Hospitality' },
];

export const DEFAULT_CHANNEL_COLORS = {
  party: '#8fd4b8',
  travel: '#6eb6ff',
  prod: '#c08133',
  hosp: '#9b7fe8',
};

export function emptyChatChannels() {
  return { party: [], travel: [], prod: [], hosp: [] };
}

/** null = everyone currently on the party */
export function emptyChatMembers() {
  return { party: null, travel: null, prod: null, hosp: null };
}

export function emptyChatReads() {
  return { party: {}, travel: {}, prod: {}, hosp: {} };
}

function normalizePersonName(value) {
  return String(value || '').trim().toLowerCase();
}

function laterIso(a, b) {
  const left = Date.parse(String(a || ''));
  const right = Date.parse(String(b || ''));
  if (!Number.isFinite(left)) return b || a || '';
  if (!Number.isFinite(right)) return a || b || '';
  return right >= left ? b : a;
}

function normalizeChatMessage(m) {
  return {
    id: String(m?.id || ''),
    who: String(m?.who || 'Member'),
    uid: String(m?.uid || ''),
    photoURL: String(m?.photoURL || '').trim(),
    text: String(m?.text || ''),
    at: String(m?.at || m?.time || ''),
  };
}

export function chatMemberKey(member) {
  const id = String(member?.id || member?.uid || '').trim();
  if (id) return id;
  return String(member?.email || '').trim().toLowerCase();
}

function normalizeHexColor(raw, fallback) {
  const s = String(raw || '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(s)) return s.toLowerCase();
  return fallback;
}

export function parseChatDoc(raw) {
  const empty = emptyChatChannels();
  if (raw == null || String(raw).trim() === '') return empty;
  try {
    const o = JSON.parse(String(raw));
    const src = o && typeof o === 'object' ? (o.channels || o) : {};
    const next = emptyChatChannels();
    Object.keys(next).forEach((id) => {
      const rows = Array.isArray(src[id]) ? src[id] : [];
      next[id] = rows
        .map(normalizeChatMessage)
        .filter((m) => m.text);
    });
    return next;
  } catch {
    return empty;
  }
}

export function parseChatMembers(rawMembers) {
  const next = emptyChatMembers();
  if (!rawMembers || typeof rawMembers !== 'object') return next;
  Object.keys(next).forEach((id) => {
    if (!Object.prototype.hasOwnProperty.call(rawMembers, id)) return;
    const row = rawMembers[id];
    if (row == null) {
      next[id] = null;
      return;
    }
    if (!Array.isArray(row)) return;
    next[id] = row.map((v) => String(v || '').trim()).filter(Boolean);
  });
  return next;
}

export function parseChannelColors(rawColors) {
  const next = { ...DEFAULT_CHANNEL_COLORS };
  if (!rawColors || typeof rawColors !== 'object') return next;
  Object.keys(next).forEach((id) => {
    next[id] = normalizeHexColor(rawColors[id], next[id]);
  });
  return next;
}

export function parseChatReads(rawReads) {
  const next = emptyChatReads();
  if (!rawReads || typeof rawReads !== 'object') return next;
  Object.keys(next).forEach((channelId) => {
    const row = rawReads[channelId];
    if (!row || typeof row !== 'object' || Array.isArray(row)) return;
    const mapped = {};
    Object.keys(row).forEach((uid) => {
      const key = String(uid || '').trim();
      const at = String(row[uid] || '').trim();
      if (key && at) mapped[key] = at;
    });
    next[channelId] = mapped;
  });
  return next;
}

export function mergeChatReads(base, incoming) {
  const left = parseChatReads(base);
  const right = parseChatReads(incoming);
  const next = emptyChatReads();
  Object.keys(next).forEach((channelId) => {
    const uids = new Set([
      ...Object.keys(left[channelId] || {}),
      ...Object.keys(right[channelId] || {}),
    ]);
    const mapped = {};
    uids.forEach((uid) => {
      mapped[uid] = laterIso(left[channelId]?.[uid], right[channelId]?.[uid]);
    });
    next[channelId] = mapped;
  });
  return next;
}

export function markChannelRead(reads, channelId, uid, atIso) {
  const next = parseChatReads(reads);
  const id = String(channelId || 'party');
  const bucket = next[id] ? id : 'party';
  const userId = String(uid || '').trim();
  const at = String(atIso || '').trim();
  if (!userId || !at) return next;
  const current = next[bucket][userId];
  next[bucket] = { ...next[bucket], [userId]: laterIso(current, at) };
  return next;
}

function chatMessageKey(message) {
  const id = String(message?.id || '').trim();
  if (id) return id;
  return [message?.uid || '', message?.at || '', message?.text || ''].join('|');
}

export function preferLongerChatChannels(base, incoming) {
  const left = { ...emptyChatChannels(), ...(base || {}) };
  const right = { ...emptyChatChannels(), ...(incoming || {}) };
  const next = emptyChatChannels();
  Object.keys(next).forEach((id) => {
    const map = new Map();
    [...(Array.isArray(left[id]) ? left[id] : []), ...(Array.isArray(right[id]) ? right[id] : [])]
      .forEach((row) => {
        const key = chatMessageKey(row);
        if (key && !map.has(key)) map.set(key, row);
      });
    next[id] = [...map.values()].sort((a, b) => {
      const aAt = Date.parse(String(a?.at || ''));
      const bAt = Date.parse(String(b?.at || ''));
      const aOk = Number.isFinite(aAt);
      const bOk = Number.isFinite(bAt);
      if (aOk && bOk && aAt !== bAt) return aAt - bAt;
      return String(a?.id || '').localeCompare(String(b?.id || ''));
    });
  });
  return next;
}

export function isOwnChatMessage(message, currentUserId, currentUserName) {
  const uid = String(message?.uid || '').trim();
  const me = String(currentUserId || '').trim();
  if (uid) return Boolean(me && uid === me);
  const who = normalizePersonName(message?.who);
  const name = normalizePersonName(currentUserName);
  return Boolean(who && name && who === name);
}

export function chatMemberForMessage(message, partyMembers = []) {
  const uid = String(message?.uid || '').trim();
  const who = normalizePersonName(message?.who);
  const list = partyMembers || [];
  if (uid) {
    const byId = list.find((m) => chatMemberKey(m) === uid || String(m?.id || '') === uid);
    if (byId) return byId;
  }
  if (who) {
    return list.find((m) => normalizePersonName(m?.name || m?.displayName) === who) || null;
  }
  return null;
}

export function readersForMessage(reads, channelId, message, channelMessages = [], partyMembers = []) {
  const sender = chatMemberForMessage(message, partyMembers);
  const senderKey = sender ? chatMemberKey(sender) : String(message?.uid || '').trim();
  const channelReads = parseChatReads(reads)[channelId] || {};
  const rows = Array.isArray(channelMessages) ? channelMessages : [];
  const msgAt = Date.parse(String(message?.at || ''));
  if (!Number.isFinite(msgAt)) return [];

  const messageUid = String(message?.uid || '').trim();
  const senderName = normalizePersonName(message?.who);
  return Object.keys(channelReads)
    .filter((uid) => {
      if (!uid) return false;
      if (senderKey && uid === senderKey) return false;
      if (messageUid && uid === messageUid) return false;
      return true;
    })
    .map((uid) => {
      const readAt = Date.parse(String(channelReads[uid] || ''));
      if (!Number.isFinite(readAt) || readAt < msgAt) return null;
      const latestSeen = [...rows].reverse().find((row) => {
        const at = Date.parse(String(row?.at || ''));
        return Number.isFinite(at) && at <= readAt;
      });
      if (!latestSeen || String(latestSeen.id || '') !== String(message?.id || '')) return null;
      const member = (partyMembers || []).find((m) => chatMemberKey(m) === uid || String(m?.id || '') === uid);
      if (member && senderKey && chatMemberKey(member) === senderKey) return null;
      if (member && senderName && normalizePersonName(member.name || member.displayName) === senderName) return null;
      return member || { id: uid, name: uid };
    })
    .filter(Boolean);
}

export function collectChatMessageIds(channels) {
  const ids = new Set();
  Object.values(channels || {}).forEach((rows) => {
    (rows || []).forEach((m) => {
      if (m?.id) ids.add(String(m.id));
    });
  });
  return ids;
}

export function shouldPlayChatChime(previousIds, incomingChannels, currentUserId, currentUserName) {
  if (!previousIds || previousIds.size === 0) return false;
  const incomingIds = collectChatMessageIds(incomingChannels);
  const overlap = [...previousIds].some((id) => incomingIds.has(id));
  if (!overlap) return false;
  const rows = Object.values(incomingChannels || {}).flat();
  return rows.some((m) => {
    const id = String(m?.id || '');
    if (!id || previousIds.has(id)) return false;
    return !isOwnChatMessage(m, currentUserId, currentUserName);
  });
}

export function parseChatState(raw) {
  let members = emptyChatMembers();
  let channelColors = { ...DEFAULT_CHANNEL_COLORS };
  let reads = emptyChatReads();
  if (raw != null && String(raw).trim()) {
    try {
      const o = JSON.parse(String(raw));
      if (o && typeof o === 'object') {
        members = parseChatMembers(o.members);
        channelColors = parseChannelColors(o.channelColors);
        reads = parseChatReads(o.reads);
      }
    } catch {
      /* messages still parse below */
    }
  }
  return { channels: parseChatDoc(raw), members, channelColors, reads };
}

export function serializeChatDoc(channels, extra = {}) {
  const base = emptyChatChannels();
  const src = channels && typeof channels === 'object' ? channels : {};
  Object.keys(base).forEach((id) => {
    base[id] = Array.isArray(src[id]) ? src[id] : [];
  });
  const payload = { v: 2, channels: base };
  if (extra.members !== undefined) payload.members = parseChatMembers(extra.members);
  if (extra.channelColors !== undefined) payload.channelColors = parseChannelColors(extra.channelColors);
  if (extra.reads !== undefined) payload.reads = parseChatReads(extra.reads);
  return JSON.stringify(payload);
}

export function serializeChatState(state) {
  return serializeChatDoc(state?.channels, {
    members: state?.members ?? emptyChatMembers(),
    channelColors: state?.channelColors ?? DEFAULT_CHANNEL_COLORS,
    reads: state?.reads ?? emptyChatReads(),
  });
}

export function isMemberOnChannel(membersMap, channelId, member) {
  const list = membersMap?.[channelId];
  if (list == null) return true;
  const key = chatMemberKey(member);
  const email = String(member?.email || '').trim().toLowerCase();
  return list.some((id) => id === key || (email && String(id).toLowerCase() === email));
}

export function membersForChannel(membersMap, channelId, partyMembers = []) {
  return (partyMembers || []).filter((m) => isMemberOnChannel(membersMap, channelId, m));
}

export function toggleChatMember(membersMap, channelId, member, partyMembers = []) {
  if (!Object.prototype.hasOwnProperty.call(emptyChatMembers(), channelId)) {
    return membersMap || emptyChatMembers();
  }
  const key = chatMemberKey(member);
  if (!key) return membersMap || emptyChatMembers();
  const current = membersMap?.[channelId];
  const materialized = current == null
    ? (partyMembers || []).map(chatMemberKey).filter(Boolean)
    : [...current];
  const has = materialized.some((id) => id === key);
  const nextList = has ? materialized.filter((id) => id !== key) : [...materialized, key];
  return { ...(membersMap || emptyChatMembers()), [channelId]: nextList };
}

/** Total messages across every tour chat channel. */
export function countChatMessages(raw) {
  const channels = parseChatDoc(raw);
  return Object.values(channels).reduce((n, rows) => n + (Array.isArray(rows) ? rows.length : 0), 0);
}

export function appendChatMessage(channels, channelId, message) {
  const next = parseChatDoc(JSON.stringify({ channels: channels || emptyChatChannels() }));
  const id = String(channelId || 'party');
  const bucket = next[id] ? id : 'party';
  next[bucket] = [...(next[bucket] || []), message];
  return next;
}
